"use client";

import Link from "next/link";
import { useTranslations, useLocale } from "next-intl";
import {
	Video,
	Music,
	Scissors,
	Layers,
	Zap,
	Shield,
	Wifi,
	Heart,
	FileArchive,
	Combine,
	Images,
	SearchCode,
	KeyRound,
	Code,
} from "lucide-react";

const toolConfig = [
	{
		key: "convert",
		href: "/converter",
		icon: Video,
		color: "bg-blue-100 dark:bg-blue-900",
	},
	{
		key: "compress",
		href: "/compressor",
		icon: FileArchive,
		color: "bg-green-100 dark:bg-green-900",
	},
	{
		key: "trim",
		href: "/trim",
		icon: Scissors,
		color: "bg-purple-100 dark:bg-purple-900",
	},
	{
		key: "stitch",
		href: "/stitch",
		icon: Combine,
		color: "bg-amber-100 dark:bg-amber-900",
	},
	{
		key: "audio",
		href: "/audio",
		icon: Music,
		color: "bg-indigo-100 dark:bg-indigo-900",
	},
	{
		key: "watermark",
		href: "/watermark",
		icon: Layers,
		color: "bg-red-100 dark:bg-red-900",
	},
	{
		key: "metadata",
		href: "/metadata",
		icon: SearchCode,
		color: "bg-teal-100 dark:bg-teal-900",
	},
	{
		key: "imageConverter",
		href: "/image-converter",
		icon: Images,
		color: "bg-orange-100 dark:bg-orange-900",
	},
	{
		key: "passwordGenerator",
		href: "/password-generator",
		icon: KeyRound,
		color: "bg-pink-100 dark:bg-pink-900",
	},
	{
		key: "base64",
		href: "/base64",
		icon: Code,
		color: "bg-cyan-100 dark:bg-cyan-900",
	},
];

export default function HomePage() {
	const locale = useLocale();
	const t = useTranslations("home");
	const tTools = useTranslations("tools");
	const tFeatures = useTranslations("features");
	const tFaq = useTranslations("faq");

	const faqs = [
		{ key: "q1" },
		{ key: "q2" },
		{ key: "q3" },
		{ key: "q4" },
		{ key: "q5" },
	];

	const faqSchema = {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: faqs.map((faq) => ({
			"@type": "Question",
			name: tFaq(`${faq.key}.question`),
			acceptedAnswer: {
				"@type": "Answer",
				text: tFaq(`${faq.key}.answer`),
			},
		})),
	};

	return (
		<>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
			/>
			<div className="max-w-4xl mx-auto">
				{/* Hero Section */}
				<section className="text-center mb-12">
					<h1 className="text-4xl md:text-5xl font-bold mb-4">
						{t("heroTitle")}
					</h1>
					<p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-6">
						{t("heroDescription")}
					</p>
					<p className="text-muted-foreground max-w-2xl mx-auto">
						{t("heroSubtext")}
					</p>
				</section>

				{/* Tools Grid */}
				<section className="mb-16">
					<h2 className="text-2xl font-bold mb-6 text-center">{t("ourTools")}</h2>
					<div className="grid gap-6 md:grid-cols-3">
						{toolConfig.map((tool) => (
							<Link
								key={tool.href}
								href={`/${locale}${tool.href}`}
								className="neo-card p-3 sm:p-6 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0_0_black] dark:hover:shadow-[4px_4px_0_0_white] transition-all"
							>
								<div
									className={`w-12 h-12 rounded-lg ${tool.color} flex items-center justify-center mb-4`}
								>
									<tool.icon className="w-6 h-6" />
								</div>
								<h3 className="text-xl font-bold mb-2">{tTools(`${tool.key}.title`)}</h3>
								<p className="text-muted-foreground">{tTools(`${tool.key}.description`)}</p>
							</Link>
						))}
					</div>
				</section>

				{/* Why FFmpeg.guru Section */}
				<section className="neo-card p-4 sm:p-8 mb-16">
					<h2 className="text-2xl font-bold mb-6">{t("whyTitle")}</h2>
					<div className="grid gap-6 md:grid-cols-2">
						<div className="flex gap-4">
							<div className="flex-shrink-0">
								<Shield className="w-8 h-8 text-green-600" />
							</div>
							<div>
								<h3 className="font-semibold mb-2">{tFeatures("private.title")}</h3>
								<p className="text-muted-foreground">
									{tFeatures("private.description")}
								</p>
							</div>
						</div>
						<div className="flex gap-4">
							<div className="flex-shrink-0">
								<Wifi className="w-8 h-8 text-blue-600" />
							</div>
							<div>
								<h3 className="font-semibold mb-2">{tFeatures("offline.title")}</h3>
								<p className="text-muted-foreground">
									{tFeatures("offline.description")}
								</p>
							</div>
						</div>
						<div className="flex gap-4">
							<div className="flex-shrink-0">
								<Heart className="w-8 h-8 text-red-600" />
							</div>
							<div>
								<h3 className="font-semibold mb-2">{tFeatures("free.title")}</h3>
								<p className="text-muted-foreground">
									{tFeatures("free.description")}
								</p>
							</div>
						</div>
						<div className="flex gap-4">
							<div className="flex-shrink-0">
								<Zap className="w-8 h-8 text-yellow-600" />
							</div>
							<div>
								<h3 className="font-semibold mb-2">{tFeatures("quality.title")}</h3>
								<p className="text-muted-foreground">
									{tFeatures("quality.description")}
								</p>
							</div>
						</div>
					</div>
				</section>

				{/* How It Works Section */}
				<section className="mb-16">
					<h2 className="text-2xl font-bold mb-6 text-center">{t("howItWorks")}</h2>
					<div className="neo-card p-4 sm:p-8">
						<p className="text-muted-foreground mb-4">
							{t("howItWorksText1")}
						</p>
						<p className="text-muted-foreground mb-4">
							{t("howItWorksText2")}
						</p>
						<p className="text-muted-foreground">
							{t("howItWorksText3")}
						</p>
						<p className="text-sm text-muted-foreground mt-4">
							{t.rich("poweredBy", {
								link: (chunks) => (
									<a href="https://ffmpeg.org" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground transition-colors">
										{chunks}
									</a>
								),
							})}
						</p>
					</div>
				</section>

				{/* FAQ Section */}
				<section className="mb-16">
					<h2 className="text-2xl font-bold mb-6 text-center">
						{t("faqTitle")}
					</h2>
					<div className="space-y-4">
						{faqs.map((faq) => (
							<div key={faq.key} className="neo-card p-3 sm:p-6">
								<h3 className="font-semibold mb-2">{tFaq(`${faq.key}.question`)}</h3>
								<p className="text-muted-foreground">{tFaq(`${faq.key}.answer`)}</p>
							</div>
						))}
					</div>
				</section>
			</div>
		</>
	);
}
